import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import type { Device, Session } from '@comms/shared';
import { useStore } from '../../store';
import { api, uploadFile } from '../../lib/api';
import { getEngine } from '../../lib/e2ee';
import { logout } from '../../lib/session';
import { listDevices, setDevice, setLeveling, useVoice } from '../../lib/voice';
import { CallVolume } from '../CallVolume';
import { disablePush, enablePush, getPushState, type PushState } from '../../lib/push';
import { isIos, promptInstall, useInstall } from '../../lib/install';
import { androidAppHasPush, isAndroidApp, nativeApp, type NativePushState } from '../../lib/native';
import {
  PushNotSetUpError,
  disableNativePush,
  enableNativePush,
  nativePushOn,
} from '../../lib/nativeApp';
import { getSoundSettings, ping, setSoundSettings } from '../../lib/sounds';
import { Avatar, Button, ErrorText, Input, Modal, Tabs } from '../ui';
import { DeveloperSettings } from './Bots';

type Tab =
  'profile' | 'security' | 'devices' | 'voice' | 'notifications' | 'appearance' | 'developer';

export function UserSettingsModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<Tab>('profile');
  return (
    <Modal title="User Settings" onClose={onClose} wide>
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'profile', label: 'Profile' },
          { id: 'security', label: 'Security' },
          { id: 'devices', label: 'Sessions & Devices' },
          { id: 'voice', label: 'Voice & Video' },
          { id: 'notifications', label: 'Notifications' },
          { id: 'appearance', label: 'Appearance' },
          { id: 'developer', label: 'Developer' },
        ]}
      />
      <div className="mt-6">
        {tab === 'profile' && <Profile />}
        {tab === 'security' && <Security />}
        {tab === 'devices' && <Devices />}
        {tab === 'voice' && <VoiceSettings />}
        {tab === 'notifications' && <NotificationSettings />}
        {tab === 'appearance' && <Appearance />}
        {tab === 'developer' && <DeveloperSettings />}
      </div>
    </Modal>
  );
}

function Profile() {
  const me = useStore((s) => s.me!);
  const [displayName, setDisplayName] = useState(me.displayName);
  const [bio, setBio] = useState(me.bio);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  async function save(extra: Record<string, unknown> = {}) {
    try {
      setError(null);
      await api('PATCH', '/users/@me', { displayName, bio, ...extra });
      setSaved(true);
    } catch (err) {
      setError(err);
    }
  }
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Avatar userId={me.id} size={80} />
        <div className="space-x-2">
          <label className="cursor-pointer rounded bg-accent px-3 py-2 text-sm text-white hover:bg-accent-hover">
            Change avatar
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              hidden
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                try {
                  const att = await uploadFile(f, f.name);
                  await save({ avatarAttachmentId: att.id });
                } catch (err) {
                  setError(err);
                }
              }}
            />
          </label>
          {me.avatarUrl && (
            <Button variant="ghost" onClick={() => void save({ avatarAttachmentId: null })}>
              Remove
            </Button>
          )}
        </div>
      </div>
      <p className="text-sm text-text-3">
        Username: <span className="text-text-1">{me.username}</span>
      </p>
      <Input
        label="Display name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        maxLength={32}
      />
      <label className="block">
        <span className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-text-2">
          About me
        </span>
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={190}
          rows={3}
          className="w-full resize-none rounded bg-bg-0 p-3 text-text-0 outline-none"
        />
      </label>
      <ErrorText error={error} />
      <Button onClick={() => void save()}>{saved ? 'Saved' : 'Save'}</Button>
    </div>
  );
}

function Security() {
  const me = useStore((s) => s.me!);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [pwMsg, setPwMsg] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [setup, setSetup] = useState<{ secret: string; qr: string } | null>(null);
  const [code, setCode] = useState('');
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState(me.mfaEnabled);

  async function changePassword() {
    try {
      setError(null);
      await api('POST', '/auth/password', { currentPassword: current, newPassword: next });
      setPwMsg('Password changed. Other sessions were signed out.');
      setCurrent('');
      setNext('');
    } catch (err) {
      setError(err);
    }
  }
  async function startMfa() {
    try {
      const res = await api<{ secret: string; otpauthUrl: string }>('POST', '/auth/mfa/setup');
      setSetup({
        secret: res.secret,
        qr: await QRCode.toDataURL(res.otpauthUrl, { margin: 1, width: 180 }),
      });
    } catch (err) {
      setError(err);
    }
  }
  async function enableMfa() {
    try {
      const res = await api<{ recoveryCodes: string[] }>('POST', '/auth/mfa/enable', { code });
      setRecovery(res.recoveryCodes);
      setSetup(null);
      setMfaEnabled(true);
    } catch (err) {
      setError(err);
    }
  }
  async function disableMfa() {
    const password = prompt('Your password');
    const c = password && prompt('A current 2FA code (or recovery code)');
    if (!password || !c) return;
    try {
      await api('POST', '/auth/mfa/disable', { password, code: c });
      setMfaEnabled(false);
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <h3 className="font-semibold text-text-0">Password</h3>
        <Input
          type="password"
          label="Current password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          autoComplete="current-password"
        />
        <Input
          type="password"
          label="New password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
          minLength={10}
          autoComplete="new-password"
        />
        <Button disabled={!current || next.length < 10} onClick={changePassword}>
          Change password
        </Button>
        {pwMsg && <p className="text-sm text-success">{pwMsg}</p>}
      </section>
      <section className="space-y-3">
        <h3 className="font-semibold text-text-0">Two-factor authentication</h3>
        {recovery && (
          <div className="rounded bg-bg-1 p-4">
            <p className="mb-2 text-sm text-text-0">
              Save these recovery codes somewhere safe. Each works once if you lose your
              authenticator.
            </p>
            <pre className="grid grid-cols-2 gap-1 font-mono text-sm text-text-1">
              {recovery.join('\n')}
            </pre>
          </div>
        )}
        {mfaEnabled ? (
          <div className="flex items-center gap-3">
            <span className="text-sm text-success">Enabled</span>
            <Button variant="danger" onClick={disableMfa}>
              Disable 2FA
            </Button>
          </div>
        ) : setup ? (
          <div className="flex gap-4">
            <img src={setup.qr} alt="2FA QR code" className="rounded bg-white p-1" />
            <div className="space-y-2">
              <p className="text-sm text-text-2">
                Scan with an authenticator app, or enter this key:
              </p>
              <code className="block break-all text-sm text-text-0">{setup.secret}</code>
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="6-digit code"
                inputMode="numeric"
              />
              <Button disabled={code.length !== 6} onClick={enableMfa}>
                Verify &amp; enable
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={startMfa}>Enable 2FA</Button>
        )}
      </section>
      <ErrorText error={error} />
    </div>
  );
}

function Devices() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const thisDevice = getEngine()?.deviceId;
  const load = () => {
    void api<Session[]>('GET', '/auth/sessions').then(setSessions);
    void api<Device[]>('GET', '/e2ee/devices').then(setDevices);
  };
  useEffect(load, []);
  return (
    <div className="space-y-8">
      <section>
        <h3 className="mb-2 font-semibold text-text-0">Active sessions</h3>
        <p className="mb-3 text-sm text-text-3">
          Signing a session out also revokes its encryption device, so it can't read new DMs.
        </p>
        {sessions.map((s) => (
          <div key={s.id} className="flex items-center gap-3 border-b border-bg-3 py-2 text-sm">
            <div className="flex-1">
              <div className="text-text-0">
                {s.name}{' '}
                {s.current && (
                  <span className="ml-1 rounded bg-success/20 px-1 text-xs text-success">
                    this session
                  </span>
                )}
              </div>
              <div className="text-xs text-text-3">
                {s.ip ?? 'unknown IP'} · last active {new Date(s.lastUsedAt).toLocaleString()}
              </div>
            </div>
            {s.current ? (
              <Button variant="ghost" className="py-1" onClick={() => void logout()}>
                Log out
              </Button>
            ) : (
              <Button
                variant="danger"
                className="py-1"
                onClick={() => void api('DELETE', `/auth/sessions/${s.id}`).then(load)}
              >
                Sign out
              </Button>
            )}
          </div>
        ))}
      </section>
      <section>
        <h3 className="mb-2 font-semibold text-text-0">Encryption devices</h3>
        <p className="mb-3 text-sm text-text-3">
          Each browser or app you use has its own keys. Compare fingerprints in a DM's verify
          screen.
        </p>
        {devices.map((d) => (
          <div key={d.id} className="flex items-center gap-3 border-b border-bg-3 py-2 text-sm">
            <div className="flex-1">
              <div className="text-text-0">
                {d.name}{' '}
                {d.id === thisDevice && (
                  <span className="ml-1 rounded bg-success/20 px-1 text-xs text-success">
                    this device
                  </span>
                )}
              </div>
              <code className="text-xs text-text-3">{d.identityKey.slice(0, 24)}…</code>
            </div>
            {d.id !== thisDevice && (
              <Button
                variant="danger"
                className="py-1"
                onClick={() => void api('DELETE', `/e2ee/devices/${d.id}`).then(load)}
              >
                Revoke
              </Button>
            )}
          </div>
        ))}
      </section>
    </div>
  );
}

function Appearance() {
  const [theme, setTheme] = useState(() => document.documentElement.dataset.theme ?? 'system');
  function apply(t: string) {
    setTheme(t);
    if (t === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = t;
    try {
      if (t === 'system') localStorage.removeItem('theme');
      else localStorage.setItem('theme', t);
    } catch {
      /* ignore */
    }
  }
  return (
    <div className="space-y-4">
      <h3 className="font-semibold text-text-0">Theme</h3>
      <div className="flex gap-2">
        {['system', 'dark', 'light'].map((t) => (
          <Button key={t} variant={theme === t ? 'primary' : 'secondary'} onClick={() => apply(t)}>
            {t[0]!.toUpperCase() + t.slice(1)}
          </Button>
        ))}
      </div>
      <h3 className="pt-4 font-semibold text-text-0">Desktop notifications</h3>
      <p className="text-sm text-text-3">
        Get notified about mentions and DMs while this tab is in the background.
      </p>
      <Button variant="secondary" onClick={() => void Notification.requestPermission()}>
        {typeof Notification !== 'undefined' && Notification.permission === 'granted'
          ? 'Enabled'
          : 'Enable notifications'}
      </Button>
    </div>
  );
}

function VoiceSettings() {
  const leveling = useVoice((s) => s.leveling);
  const [devices, setDevices] = useState<Record<string, MediaDeviceInfo[]>>({});
  const [error, setError] = useState<unknown>(null);
  const kinds: { kind: 'audioinput' | 'audiooutput' | 'videoinput'; label: string; key: string }[] =
    [
      { kind: 'audioinput', label: 'Input device (microphone)', key: 'voice.inputDevice' },
      { kind: 'audiooutput', label: 'Output device (speakers)', key: 'voice.outputDevice' },
      { kind: 'videoinput', label: 'Camera', key: 'voice.videoDevice' },
    ];
  async function load() {
    try {
      const out: Record<string, MediaDeviceInfo[]> = {};
      for (const k of kinds) out[k.kind] = await listDevices(k.kind);
      setDevices(out);
    } catch (err) {
      setError(err);
    }
  }
  useEffect(() => void load(), []); // eslint-disable-line react-hooks/exhaustive-deps
  const current = (key: string) => {
    try {
      return localStorage.getItem(key) ?? '';
    } catch {
      return '';
    }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-3">
        Your browser may ask for microphone/camera permission the first time so device names can be
        shown.
      </p>
      {kinds.map((k) => (
        <label key={k.kind} className="block text-sm text-text-2">
          {k.label}
          <select
            defaultValue={current(k.key)}
            onChange={(e) => void setDevice(k.kind, e.target.value).catch(setError)}
            className="mt-1 w-full rounded bg-bg-0 p-2 text-text-0"
          >
            <option value="">System default</option>
            {(devices[k.kind] ?? []).map((d) => (
              <option key={d.deviceId} value={d.deviceId}>
                {d.label || `${k.label} ${d.deviceId.slice(0, 6)}`}
              </option>
            ))}
          </select>
        </label>
      ))}
      <label className="flex items-start gap-2 text-sm text-text-1">
        <input
          type="checkbox"
          className="mt-1"
          checked={leveling}
          onChange={(e) => setLeveling(e.target.checked)}
        />
        <span>
          Even out my voice volume (experimental)
          <span className="block text-text-3">
            Makes a quiet microphone easier to hear and keeps loud moments from distorting. Not
            recommended on iPhone. If others can't hear you, turn it off.
          </span>
        </span>
      </label>
      <div className="text-sm text-text-2">
        Call volume (how loud others sound to you)
        <CallVolume className="mt-1 text-sm" />
        <span className="mt-1 block text-xs text-text-3">
          Above 100% is a boost. On a computer without headphones it can cause echo.
        </span>
      </div>
      <p className="text-sm text-text-3">
        Echo cancellation, noise suppression and automatic gain are on by default.
      </p>
      <ErrorText error={error} />
    </div>
  );
}

/** Install as an app (home screen / desktop): the browser's prompt, or how-to steps on iOS. */
function InstallApp() {
  const { prompt, installed } = useInstall();
  return (
    <div className="flex items-start justify-between gap-4 rounded bg-bg-1 p-4">
      <div>
        <h3 className="font-semibold text-text-0">App on this device</h3>
        <p className="mt-1 text-sm text-text-3">
          {installed
            ? "You're using the installed app."
            : 'Install Comms to open it from your home screen or dock, in its own window.'}
        </p>
        {!installed && !prompt && isIos() && (
          <p className="mt-2 text-sm text-text-2">
            In Safari, tap Share, then <b>Add to Home Screen</b>. On iPhone and iPad, push
            notifications only work from the installed app.
          </p>
        )}
        {!installed && !prompt && !isIos() && (
          <p className="mt-2 text-sm text-text-2">
            Use your browser's menu: <b>Install app</b> or <b>Add to Home screen</b>.
          </p>
        )}
      </div>
      {prompt && !installed && <Button onClick={() => void promptInstall()}>Install app</Button>}
    </div>
  );
}

/** The Android app from this server: a link people can open (or share) on their phone. */
function AndroidAppLink() {
  if (isAndroidApp()) return null;
  return (
    <div className="rounded bg-bg-1 p-4">
      <h3 className="font-semibold text-text-0">Android app</h3>
      <p className="mt-1 text-sm text-text-3">
        Calls that ring like phone calls, and notifications even when it&apos;s closed. Open{' '}
        <a href="/android" className="text-accent hover:underline">
          {window.location.host}/android
        </a>{' '}
        on your phone to get it.
      </p>
    </div>
  );
}

/** Inside the Android app: phone notifications through the app (Firebase). */
function PhoneNotificationSettings() {
  const [on, setOn] = useState(nativePushOn());
  const [phone, setPhone] = useState<NativePushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [notSetUp, setNotSetUp] = useState(false);
  const refresh = () =>
    void nativeApp
      .pushState()
      .then(setPhone)
      .catch(() => {});
  useEffect(refresh, []);
  async function toggle() {
    setBusy(true);
    setError(null);
    setNotSetUp(false);
    try {
      if (on) {
        await disableNativePush();
        setOn(false);
      } else {
        await enableNativePush();
        setOn(true);
      }
    } catch (err) {
      if (err instanceof PushNotSetUpError) setNotSetUp(true);
      else setError(err);
    } finally {
      setBusy(false);
      refresh();
    }
  }
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4 rounded bg-bg-1 p-4">
        <div>
          <h3 className="font-semibold text-text-0">Phone notifications</h3>
          <p className="mt-1 text-sm text-text-3">
            Mentions, DMs and incoming calls on this phone while the app is closed or in the
            background. Incoming calls ring like a phone call. Encrypted DMs only ever say "New
            message", and notifications are encrypted so Google can't read them.
          </p>
          {notSetUp && (
            <p className="mt-2 text-sm text-warning">
              This server isn't set up for phone notifications yet. The server's admin needs to add
              a Firebase project (see "Native push" in the server's README).
            </p>
          )}
          {on && phone?.permission === 'denied' && (
            <p className="mt-2 text-sm text-warning">
              Notifications are turned off for Comms in Android's settings.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => nativeApp.openSettings('notifications')}
              >
                Open settings
              </button>
            </p>
          )}
          {on && phone?.permission === 'granted' && !phone.fullScreen && (
            <p className="mt-2 text-sm text-warning">
              Incoming calls can't ring full-screen on the lock screen yet.{' '}
              <button
                type="button"
                className="underline"
                onClick={() => nativeApp.openSettings('full-screen')}
              >
                Allow full-screen calls
              </button>
            </p>
          )}
        </div>
        <Button
          variant={on ? 'secondary' : 'primary'}
          disabled={busy}
          onClick={() => void toggle()}
        >
          {busy ? '…' : on ? 'Turn off' : 'Turn on'}
        </Button>
      </div>
      <ErrorText error={error} />
    </div>
  );
}

function NotificationSettings() {
  const [state, setState] = useState<PushState | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => void getPushState().then(setState), []);
  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      setState(state === 'on' ? await disablePush() : await enablePush());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }
  // The Android app has no web push; it gets its own phone notifications in an app update.
  if (isAndroidApp() && androidAppHasPush()) return <PhoneNotificationSettings />;
  if (isAndroidApp())
    return (
      <div className="rounded bg-bg-1 p-4">
        <h3 className="font-semibold text-text-0">Notifications on this phone</h3>
        <p className="mt-1 text-sm text-text-3">
          <a href="/android" className="text-accent hover:underline">
            Update the Comms app
          </a>{' '}
          to get phone notifications for mentions, DMs and incoming calls. Until then, notifications
          reach your other devices as usual.
        </p>
      </div>
    );
  return (
    <div className="space-y-4">
      <InstallApp />
      <AndroidAppLink />
      <div className="flex items-start justify-between gap-4 rounded bg-bg-1 p-4">
        <div>
          <h3 className="font-semibold text-text-0">Push notifications on this device</h3>
          <p className="mt-1 text-sm text-text-3">
            Get mentions, DMs and incoming calls on this device whenever the app isn't open here,
            even while you're using it on another device. Nothing is pushed while you're on Do Not
            Disturb. Encrypted DMs only ever show "New message" — the server can't see what was
            said. The app icon shows how many mentions and DMs are unread.
          </p>
          {state === 'denied' && (
            <p className="mt-2 text-sm text-warning">
              Notifications are blocked for this site. Allow them in your browser's site settings,
              then try again.
            </p>
          )}
          {state === 'unsupported' && (
            <p className="mt-2 text-sm text-warning">
              This browser doesn't support push notifications here. On iPhone/iPad, add this site to
              your Home Screen first.
            </p>
          )}
        </div>
        <Button
          variant={state === 'on' ? 'secondary' : 'primary'}
          disabled={busy || state === null || state === 'unsupported' || state === 'denied'}
          onClick={() => void toggle()}
          aria-pressed={state === 'on'}
        >
          {state === 'on' ? 'Turn off' : 'Turn on'}
        </Button>
      </div>
      <p className="text-sm text-text-3">
        Choose what each server notifies you about from its menu (click the server name): all
        messages, only @mentions (the default), or nothing — or mute it. Any channel or thread can
        override that from the bell in its header. DMs and calls always notify unless muted.
      </p>
      <ErrorText error={error} />
      <SoundSettings />
    </div>
  );
}

/** Alert sounds while the app is open (this device only). */
function SoundSettings() {
  const [s, setS] = useState(getSoundSettings);
  const update = (patch: Parameters<typeof setSoundSettings>[0]) => setS(setSoundSettings(patch));
  return (
    <div className="space-y-3 rounded bg-bg-1 p-4">
      <h3 className="font-semibold text-text-0">Sounds on this device</h3>
      <label className="flex items-center gap-2 text-sm text-text-1">
        <input
          type="checkbox"
          checked={s.calls}
          onChange={(e) => update({ calls: e.target.checked })}
        />
        Ring for incoming calls
      </label>
      <label className="flex items-center gap-2 text-sm text-text-1">
        <input
          type="checkbox"
          checked={s.messages}
          onChange={(e) => update({ messages: e.target.checked })}
        />
        Chime for new messages and mentions (follows your notification settings)
      </label>
      <label className="flex items-center gap-3 text-sm text-text-1">
        Volume
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={s.volume}
          onChange={(e) => update({ volume: Number(e.target.value) })}
          className="flex-1"
        />
        <Button variant="secondary" className="py-1" onClick={() => ping(s.volume)}>
          Test
        </Button>
      </label>
      <p className="text-xs text-text-3">
        These play while the app is open. When it's closed, notifications use your device's sound.
        On iPhone, the silent switch also mutes these.
      </p>
    </div>
  );
}
